/**
 * Grille 7 colonnes (planning d'au moins GRID_MIN_WIDTH = 1 201 px de large, index.tsx, soit environ 1 253 px de
 * fenêtre sur monday-2026 ; SPEC PL2) : en-tête de jour collant (jour court, jj/mm, aujourd'hui à la couleur de la
 * marque), cartes du jour, tiret quand le jour est vide. En chargement : cartes en attente (Skeleton.tsx), puis les
 * cartes en fondu.
 */
import { isSameDay } from 'date-fns';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import type { PlanningClass } from '../../api/planning';
import { revealElements } from '../../lib/planning/skeleton';
import { dayLong, dayNumber, dayShort, isPastDay } from '../../lib/planning/week';
import { SkeletonList } from './Skeleton';

interface WeekGridProps {
  days: Date[];
  today: Date;
  classesByDay: PlanningClass[][];
  headingLevel: 2 | 3;
  renderCard: (c: PlanningClass) => ReactNode;
  isLoading: boolean;
  /** Vue multi-marques : les cartes en attente portent le bloc de l'étiquette de marque. */
  skeletonBrandTag: boolean;
  /** Montée à la place d'un squelette (celui du serveur) : les cartes apparaissent en fondu, comme après un chargement. */
  revealOnMount?: boolean;
}

export function WeekGrid({
  days,
  today,
  classesByDay,
  headingLevel,
  renderCard,
  isLoading,
  skeletonBrandTag,
  revealOnMount = false,
}: WeekGridProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const rootRef = useRef<HTMLDivElement>(null);
  const wasLoading = useRef(isLoading || revealOnMount);
  // Cartes arrivées à la place des cartes en attente : fondu léger (opacité seulement), en-têtes de jours immobiles.
  useLayoutEffect(() => {
    if (wasLoading.current && !isLoading) {
      revealElements(rootRef.current?.querySelectorAll('.mmv2-day__list, .mmv2-day__empty') ?? []);
    }
    wasLoading.current = isLoading;
  }, [isLoading]);
  return (
    <div ref={rootRef} className="mmv2-week-grid" aria-busy={isLoading || undefined}>
      {days.map((day, index) => {
        const isToday = isSameDay(day, today);
        const classes = classesByDay[index] ?? [];
        return (
          <section
            key={day.toISOString()}
            className="mmv2-day"
            data-today={isToday || undefined}
            data-past={isPastDay(day, today) || undefined}
          >
            <Heading className="mmv2-day__head">
              <span className="mmv2-day__name" aria-hidden="true">
                {dayShort(day)}
              </span>
              <span className="mmv2-day__date" aria-hidden="true">
                {dayNumber(day)}
              </span>
              <span className="mmv2-sr-only">
                {dayLong(day)}
                {isToday ? ', aujourd’hui' : ''}
              </span>
            </Heading>
            {isLoading ? (
              <SkeletonList className="mmv2-day__list" day={index} brandTag={skeletonBrandTag} />
            ) : classes.length ? (
              <ul className="mmv2-day__list">
                {classes.map((c) => (
                  <li key={`${c.SiteId}:${c.Id}`} className="mmv2-day__item">
                    {renderCard(c)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mmv2-day__empty">
                <span aria-hidden="true">—</span>
                <span className="mmv2-sr-only">Aucun cours</span>
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
